import React, { useEffect } from 'react';
import { SCORING_ROLES, SHARED_EVENTS } from './scoringData';

interface ScoringModalProps {
  open: boolean;
  onClose: () => void;
}

const ScoringModal: React.FC<ScoringModalProps> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: 'rgba(4,5,6,.82)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '5vh 5vw',
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 920,
          maxHeight: '88vh',
          overflowY: 'auto',
          borderRadius: 26,
          background: '#0c0f11',
          border: '1px solid rgba(107,163,255,.25)',
          padding: '38px 34px 34px',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Arcadia scoring"
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            width: 34,
            height: 34,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,.14)',
            background: 'rgba(255,255,255,.06)',
            color: '#cfd3d2',
            fontSize: 17,
            fontWeight: 700,
            fontFamily: "'Montserrat',sans-serif",
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
        <div
          style={{
            color: '#6ba3ff',
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: '.22em',
            marginBottom: 10,
          }}
        >
          ARCADIA SCORING
        </div>
        <h3
          style={{
            margin: '0 0 8px',
            color: '#fff',
            fontSize: 28,
            fontWeight: 900,
            letterSpacing: '-.02em',
          }}
        >
          Every role, scored on its own job.
        </h3>
        <p
          style={{
            margin: '0 0 26px',
            color: '#9aa09f',
            fontSize: 13.5,
            fontWeight: 500,
            lineHeight: 1.7,
            maxWidth: 640,
          }}
        >
          On top of the standard events every player shares, each Arcadia position earns{' '}
          <strong style={{ color: '#fff', fontWeight: 800 }}>+0.5 points</strong> for every set
          number of role-specific actions listed below.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))',
            gap: 12,
            marginBottom: 26,
          }}
        >
          {SCORING_ROLES.map((r) => (
            <div
              key={r.pos}
              style={{
                borderRadius: 16,
                background: 'rgba(255,255,255,.035)',
                border: '1px solid rgba(255,255,255,.07)',
                padding: '16px 16px 14px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <span
                  style={{
                    padding: '3px 8px',
                    borderRadius: 7,
                    background: r.chipBg,
                    border: `1px solid ${r.chipBorder}`,
                    color: r.color,
                    fontSize: 10,
                    fontWeight: 900,
                  }}
                >
                  {r.pos}
                </span>
                <span style={{ color: '#cfd3d2', fontSize: 11, fontWeight: 700 }}>{r.label}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 7,
                  marginBottom: 10,
                }}
              >
                {r.bonuses.map((b) => (
                  <div
                    key={`${r.pos}-${b.stat}`}
                    style={{
                      flex: 'none',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        flex: 'none',
                        color: '#71776f',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      every
                    </span>
                    <span
                      style={{
                        flex: 'none',
                        color: '#fff',
                        fontSize: 12.5,
                        fontWeight: 900,
                      }}
                    >
                      {b.every}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        color: '#9aa09f',
                        fontSize: 11.5,
                        fontWeight: 600,
                        lineHeight: 1.35,
                      }}
                    >
                      {b.stat}
                    </span>
                    <span
                      style={{
                        flex: 'none',
                        color: '#7fe09c',
                        fontSize: 11.5,
                        fontWeight: 900,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      +0.5
                    </span>
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  paddingTop: 9,
                  borderTop: '1px solid rgba(255,255,255,.06)',
                }}
              >
                <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <span style={{ color: '#7fe09c', fontSize: 12, fontWeight: 900 }}>{r.goal}</span>
                  <span
                    style={{
                      color: '#71776f',
                      fontSize: 8.5,
                      fontWeight: 800,
                      letterSpacing: '.06em',
                    }}
                  >
                    GOAL
                  </span>
                </span>
                <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <span style={{ color: '#e8b95a', fontSize: 12, fontWeight: 900 }}>{r.clean}</span>
                  <span
                    style={{
                      color: '#71776f',
                      fontSize: 8.5,
                      fontWeight: 800,
                      letterSpacing: '.06em',
                    }}
                  >
                    CLEAN SHEET
                  </span>
                </span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ color: '#fff', fontSize: 13, fontWeight: 900, marginBottom: 4 }}>
          Shared by every position
        </div>
        <p style={{ margin: '0 0 12px', color: '#71776f', fontSize: 11.5, fontWeight: 500 }}>
          Goals and clean sheets are worth more the deeper the role, as shown on each card above.
          Goalkeepers have no role bonuses: they score on the standard events.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))',
            gap: 8,
          }}
        >
          {SHARED_EVENTS.map((s) => (
            <div
              key={s.label}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 10,
                padding: '9px 13px',
                borderRadius: 11,
                background: 'rgba(255,255,255,.03)',
                border: '1px solid rgba(255,255,255,.06)',
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  color: '#9aa09f',
                  fontSize: 11.5,
                  fontWeight: 600,
                  lineHeight: 1.35,
                }}
              >
                {s.label}
              </span>
              <span style={{ color: s.color, fontSize: 11.5, fontWeight: 900 }}>{s.pts}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScoringModal;
